import { hasUnreadDiscussion } from '@/lib/discussions/discussion-unread';
import type { FeedItem } from '@/components/news/types';

/** Au-delà, le badge affiche « 9+ ». */
export const FEED_BADGE_MAX = 9;

/** Auteur de l'élément : ce qu'on a produit soi-même n'est jamais « nouveau ». */
export function actorOf(item: FeedItem): string | null {
  switch (item.kind) {
    case 'assignment':
      return item.data.tutor_id;
    case 'challenge':
      return item.data.user_id;
    case 'attempt':
    case 'attempt_started':
      return item.data.student_id;
    case 'submission':
      return item.data.student_id;
    case 'card_assignment':
      return item.data.assignment?.tutor_id ?? null;
    case 'evaluation':
    case 'diagnostic':
      return item.data.student_id;
    case 'question_report':
      return item.data.reporter_id;
    case 'private_live':
      return item.data.host?.id ?? null;
    default:
      return null;
  }
}

/**
 * Un élément du Feed est « nouveau » s'il est arrivé après la dernière ouverture
 * du Feed (`seenAt`, `profiles.feed_seen_at`) et n'a pas été produit par le
 * viewer. Une discussion doit en plus être réellement non lue (on ne recompte
 * pas une discussion déjà lue dans son fil). `seenAt` null = jamais ouvert.
 */
export function isFeedItemUnread(
  item: FeedItem,
  seenAt: string | null | undefined,
  viewerId: string | null | undefined,
): boolean {
  if (!item.date) return false;
  // Une visio passée est de l'historique : on y a participé, rien de « nouveau ».
  if (item.kind === 'visio') return false;
  const afterSeen = !seenAt || new Date(item.date).getTime() > new Date(seenAt).getTime();
  if (!afterSeen) return false;
  if (item.kind === 'discussion') {
    const role = viewerId && viewerId === item.data.tutorId ? 'tutor' : 'student';
    return hasUnreadDiscussion(item.data, role);
  }
  const actor = actorOf(item);
  return !(viewerId && actor === viewerId);
}

export function countUnreadFeedItems(
  items: readonly FeedItem[],
  seenAt: string | null | undefined,
  viewerId: string | null | undefined,
): number {
  return items.filter((i) => isFeedItemUnread(i, seenAt, viewerId)).length;
}

/** Texte du badge (« 3 », « 9+ ») ; null quand rien à signaler. */
export function formatFeedBadge(count: number): string | null {
  if (count <= 0) return null;
  return count > FEED_BADGE_MAX ? `${FEED_BADGE_MAX}+` : String(count);
}
